// src/net/recording.ts — GİRDİ KAYDI: bir maçın tüm `InputFrame`lerini saklar ve birebir yeniden oynatır.
//
// NEDEN (NETWORK.md §7.3): ağ kodu yazılmadan ÖNCE protokolün ilk testi budur. Kayıt, sunucunun istemcilere
// yayınlayacağı şeyin ta kendisidir — aynı kuantalama, aynı bayt düzeni (`inputCodec.ts`). Kayıt birebir
// oynatılabiliyorsa "otorite girdiyi yayınlar, herkes aynı dünyayı kurar" modeli ağ olmadan kanıtlanmış olur.
// Ayrıca: hata ayıklama ("şu maçta ne oldu"), determinizm regresyonu ve ileride tekrar (replay) özelliği.
//
// Kap düz veri: three'siz, DOM'suz, `structuredClone`suz. `data` tick-major: tick t, oyuncu i →
// `(t * fighters + i) * INPUT_BYTES`. 8 dövüşçü × 3 dk = 10800 × 8 × 8 B = 691 KB (ham), gzip'te çok daha az.
import type { InputFrame } from '../core/types.ts';
import { inputFrame } from '../core/types.ts';
import { MAX_FIGHTERS } from '../config/constants.ts';
import { INPUT_BYTES, decodeInput, encodeInput, quantizeInput } from './inputCodec.ts';

/** Kap sürümü: bayt düzeni, kuantalama ya da kabın alanları değişirse ARTIR (eski kayıtlar sessizce yanlış oynamasın). */
export const RECORDING_VERSION = 2;

/** Arena kimliği. Dünya kurulumunu değiştiren HER şey kayıtta olmalı, yoksa oynatma sessizce başka bir maç kurar.
 *  `net` paketi `game/tutorialMode.ts`i import edemez (katman oku), o yüzden etiket taşınıyor ve arenayı çağıran verir. */
export type ArenaTag = 'default' | 'tutorial';

export interface RecordedFighter { charIdx: number; isBot: boolean }

export interface InputRecording {
  version: number;
  /** Sim seed'i — dünya bununla kurulur (`hashNoise` tuzu). */
  seed: number;
  /** Kadro çekilişinin tohumu (`roster.ts` `rosterRand`); kadroyu yeniden üretmek için saklanır. */
  rosterSeed: number;
  fighters: RecordedFighter[];
  /** Maç geri sayımla mı başladı (DESIGN §13). Eğitimde ve bazı testlerde false — dünya kurulumunu değiştirir. */
  countdown: boolean;
  /** Hangi arena (eğitim arenasında eşya doğma noktaları boş). */
  arena: ArenaTag;
  /** Kaydedilen tick sayısı. */
  ticks: number;
  /** ticks × fighters × INPUT_BYTES. */
  data: Uint8Array;
}

/** Büyüyen kayıt tamponu (sıcak yolda tahsis yok: blok blok büyür, tick başına yalnız 8 B/oyuncu yazılır). */
export class InputRecorder {
  private buf: Uint8Array;
  private count = 0;                     // yazılan tick sayısı
  private readonly stride: number;

  constructor(readonly seed: number, readonly rosterSeed: number, readonly fighters: RecordedFighter[],
              readonly countdown = true, readonly arena: ArenaTag = 'default', capacityTicks = 3600) {
    this.stride = fighters.length * INPUT_BYTES;
    this.buf = new Uint8Array(this.stride * Math.max(1, capacityTicks));
  }

  get ticks(): number { return this.count; }

  /** Bir tick'in TÜM oyuncularının girdisini yazar. `world.step()`ten ÖNCE, girdiler yazıldıktan sonra çağrılır. */
  push(inputs: readonly InputFrame[]): void {
    if (inputs.length !== this.fighters.length) throw new Error('recorder: dövüşçü sayısı değişti');
    const need = (this.count + 1) * this.stride;
    if (need > this.buf.length) {
      // İkiye katla: 3 dakikalık bir maçta ~3 kez olur, sıcak yolda değil.
      const grown = new Uint8Array(Math.max(need, this.buf.length * 2));
      grown.set(this.buf);
      this.buf = grown;
    }
    let off = this.count * this.stride;
    for (let i = 0; i < inputs.length; i++) off += encodeInput(inputs[i]!, this.buf, off);
    this.count++;
  }

  finish(): InputRecording {
    return {
      version: RECORDING_VERSION, seed: this.seed, rosterSeed: this.rosterSeed,
      fighters: this.fighters.map((f) => ({ charIdx: f.charIdx, isBot: f.isBot })),
      countdown: this.countdown, arena: this.arena,
      ticks: this.count, data: this.buf.slice(0, this.count * this.stride),
    };
  }
}

/** Kayıttan bir tick'in girdilerini `out`a çözer (tahsissiz). Tick aralık dışındaysa false. */
export function readTick(rec: InputRecording, tick: number, out: readonly InputFrame[]): boolean {
  if (tick < 0 || tick >= rec.ticks) return false;
  const stride = rec.fighters.length * INPUT_BYTES;
  let off = tick * stride;
  for (let i = 0; i < out.length && i < rec.fighters.length; i++) off += decodeInput(rec.data, off, out[i]!);
  return true;
}

/** Kayıt kabından bir tick'lik girdi dizisi üretir (test/araç kolaylığı; tahsis eder — sıcak yolda kullanma). */
export function decodeTick(rec: InputRecording, tick: number): InputFrame[] {
  const out = rec.fighters.map(() => inputFrame());
  readTick(rec, tick, out);
  return out;
}

/**
 * Girdileri kuantalayıp kaydeder — canlı oyunda kullanılan yol.
 * `world.inputs`ın TAMAMINI yerinde kuantalar (sim'in gördüğü = telde giden), sonra kaydeder.
 * Bu sıra önemli: önce kuantala, sonra `step()`. Tersi yapılırsa kayıt oynatıldığında dünya ayrışır.
 */
export function quantizeAndRecord(inputs: readonly InputFrame[], rec: InputRecorder | null): void {
  for (let i = 0; i < inputs.length; i++) quantizeInput(inputs[i]!);
  rec?.push(inputs);
}

// ── JSON taşıma (dosyaya yaz / ağdan al) ──
// `Uint8Array` JSON'da doğrudan yok; base64 hem tarayıcıda hem Node'da bağımlılıksız çevrilebilir.

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export function toBase64(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i += 3) {
    const a0 = b[i]!, a1 = b[i + 1], a2 = b[i + 2];
    const n = (a0 << 16) | ((a1 ?? 0) << 8) | (a2 ?? 0);
    s += B64[(n >>> 18) & 63]! + B64[(n >>> 12) & 63]!;
    s += a1 === undefined ? '=' : B64[(n >>> 6) & 63]!;
    s += a2 === undefined ? '=' : B64[n & 63]!;
  }
  return s;
}

export function fromBase64(s: string): Uint8Array {
  const clean = s.replace(/[^A-Za-z0-9+/]/g, '');
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let o = 0;
  for (let i = 0; i < clean.length; i += 4) {
    const n = (B64.indexOf(clean[i]!) << 18) | (B64.indexOf(clean[i + 1]!) << 12) |
              ((clean[i + 2] === undefined ? 0 : B64.indexOf(clean[i + 2]!)) << 6) |
              (clean[i + 3] === undefined ? 0 : B64.indexOf(clean[i + 3]!));
    if (o < out.length) out[o++] = (n >>> 16) & 0xff;
    if (o < out.length) out[o++] = (n >>> 8) & 0xff;
    if (o < out.length) out[o++] = n & 0xff;
  }
  return out;
}

export interface RecordingJson {
  version: number; seed: number; rosterSeed: number;
  fighters: RecordedFighter[]; countdown: boolean; arena: ArenaTag; ticks: number; data: string;
}

export const toJson = (r: InputRecording): RecordingJson => ({
  version: r.version, seed: r.seed, rosterSeed: r.rosterSeed,
  fighters: r.fighters, countdown: r.countdown, arena: r.arena, ticks: r.ticks, data: toBase64(r.data),
});

/**
 * JSON kabını doğrular ve çözer. HER alan denetleniyor: kayıt dosyadan ya da ağdan geliyor ve yanlış bir
 * `fighters` uzunluğu (bayt boyu tesadüfen tutsa bile) tick sınırlarını kaydırıp sessizce BAŞKA bir maç oynatır.
 * Sessiz yanlış oynatma, hata vermekten çok daha kötü: determinizm hatası ararken kaybolan saatlerin kaynağı budur.
 */
export function fromJson(j: RecordingJson): InputRecording {
  if (j.version !== RECORDING_VERSION) {
    throw new Error(`kayıt sürümü ${j.version}, beklenen ${RECORDING_VERSION} — kap değişmiş, bu kayıt oynatılamaz`);
  }
  if (!Number.isInteger(j.seed) || !Number.isInteger(j.rosterSeed)) throw new Error('kayıt bozuk: seed tam sayı değil');
  if (!Number.isInteger(j.ticks) || j.ticks < 0) throw new Error('kayıt bozuk: tick sayısı geçersiz');
  if (typeof j.countdown !== 'boolean') throw new Error('kayıt bozuk: countdown eksik');
  if (j.arena !== 'default' && j.arena !== 'tutorial') throw new Error(`kayıt bozuk: bilinmeyen arena "${String(j.arena)}"`);
  if (!Array.isArray(j.fighters) || j.fighters.length < 1 || j.fighters.length > MAX_FIGHTERS) {
    throw new Error(`kayıt bozuk: dövüşçü sayısı 1..${MAX_FIGHTERS} olmalı`);
  }
  for (const f of j.fighters) {
    if (!f || !Number.isInteger(f.charIdx) || f.charIdx < 0 || typeof f.isBot !== 'boolean') {
      throw new Error('kayıt bozuk: dövüşçü kaydı geçersiz');
    }
  }
  const data = fromBase64(j.data);
  const need = j.ticks * j.fighters.length * INPUT_BYTES;
  // TAM eşitlik: fazla bayt da bir uyumsuzluk işaretidir (yanlış dövüşçü sayısı, kesilmiş base64).
  if (data.length < need) throw new Error(`kayıt bozuk: ${data.length} B var, ${need} B gerekli`);
  if (data.length > need + 2) throw new Error(`kayıt bozuk: ${data.length} B var, ${need} B bekleniyor (fazla veri)`);
  return {
    version: j.version, seed: j.seed, rosterSeed: j.rosterSeed, fighters: j.fighters,
    countdown: j.countdown, arena: j.arena, ticks: j.ticks, data,
  };
}
